import { Alert, Code, Collapse, Text } from '@mantine/core';
import { IconChevronDown, IconInfoCircle } from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { useIntl } from 'react-intl';

interface TemplateInfoAlertProps {
  fields?: any[];
}

/**
 * TemplateInfoAlert Component
 *
 * Displays information about using ChatGPT to generate templates,
 * lists the available Handlebars.js helpers, and shows available
 * field properties that can be used in templates.
 */
export const TemplateInfoAlert = ({ fields = [] }: TemplateInfoAlertProps) => {
  const intl = useIntl();
  const [opened, { toggle }] = useDisclosure(false);

  // Get available properties for each field
  const getFieldProperties = (field: any) => {
    if (!field || !field.name) return [];

    const properties: string[] = [];
    const fieldName = field.name;

    switch (field.type) {
      case 'text':
      case 'textarea':
      case 'email':
      case 'tel':
        properties.push(
          fieldName,
          `${fieldName}Label`,
          `${fieldName}Description`
        );
        break;
      case 'datepicker':
        properties.push(
          fieldName,
          `${fieldName}Label`,
          `${fieldName}Description`
        );
        break;
      case 'title':
        properties.push(`${fieldName}Title`);
        break;
      case 'disclaimer':
        properties.push(`${fieldName}Title`, `${fieldName}Description`);
        break;
      case 'radioButtonGroup':
        properties.push(
          fieldName,
          `${fieldName}Label`,
          `${fieldName}Description`
        );
        break;
      case 'checkboxGroup':
        properties.push(
          fieldName,
          `${fieldName}Label`,
          `${fieldName}Description`
        );
        break;
      case 'map':
        properties.push(
          `${fieldName}.lat`,
          `${fieldName}.lng`,
          `${fieldName}Label`,
          `${fieldName}Description`
        );
        break;
      case 'robeSizes':
        properties.push(
          `${fieldName}.xs`,
          `${fieldName}.s`,
          `${fieldName}.m`,
          `${fieldName}.l`,
          `${fieldName}.xl`,
          `${fieldName}.classMasterXS`,
          `${fieldName}.classMasterS`,
          `${fieldName}.classMasterM`,
          `${fieldName}.classMasterL`,
          `${fieldName}.classMasterXL`,
          `${fieldName}Label`,
          `${fieldName}Description`
        );
        break;
      case 'signature':
        properties.push(fieldName);
        break;
      case 'multi-level-combobox':
        if (field.customInput?.selectedValues) {
          Object.keys(field.customInput.selectedValues).forEach((key) => {
            properties.push(`${fieldName}.${key}`);
          });
        }
        if (field.customInput?.properties) {
          Object.keys(field.customInput.properties).forEach((key) => {
            properties.push(`${fieldName}.${key}`);
          });
        }
        properties.push(`${fieldName}Label`);
        break;
      case 'picker':
        properties.push(
          fieldName,
          `${fieldName}Label`,
          `${fieldName}Description`
        );
        break;
      case 'products':
        properties.push(fieldName, `${fieldName}Label`);
        break;
      case 'infinite-list':
        properties.push(
          fieldName,
          `${fieldName}Label`,
          `${fieldName}Description`
        );
        break;
      case 'imagePicker':
        properties.push(fieldName);
        break;
    }

    return properties;
  };

  return (
    <Alert
      icon={<IconInfoCircle size={16} />}
      title={intl.formatMessage({ id: 'templateInfoTitle' })}
      color="blue"
    >
      <div style={{ marginBottom: '0.5rem' }}>
        {intl.formatMessage({ id: 'templateInfoChatGPT' })}
      </div>
      <div style={{ marginBottom: '0.5rem', fontWeight: 500 }}>
        {intl.formatMessage({ id: 'templateInfoHandlebars' })}
      </div>
      <div style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
        <strong>{intl.formatMessage({ id: 'availableHelpers' })}:</strong>
        <ul
          style={{
            marginTop: '0.5rem',
            marginBottom: 0,
            paddingLeft: '1.5rem',
          }}
        >
          <li>
            <Code>sumKeys</Code> - {intl.formatMessage({ id: 'helperSumKeys' })}
          </li>
          <li>
            <Code>multiply</Code> -{' '}
            {intl.formatMessage({ id: 'helperMultiply' })}
          </li>
          <li>
            <Code>sumTotals</Code> -{' '}
            {intl.formatMessage({ id: 'helperSumTotals' })}
          </li>
          <li>
            <Code>formatDate</Code> -{' '}
            {intl.formatMessage({ id: 'helperFormatDate' })}
          </li>
          <li>
            <Code>eq</Code>, <Code>ne</Code> -{' '}
            {intl.formatMessage({ id: 'helperComparison' })}
          </li>
          <li>
            <Code>and</Code>, <Code>or</Code>, <Code>not</Code> -{' '}
            {intl.formatMessage({ id: 'helperLogical' })}
          </li>
          <li>
            <Code>startsWith</Code>, <Code>contains</Code>, <Code>eqi</Code> -{' '}
            {intl.formatMessage({ id: 'helperString' })}
          </li>
          <li>
            <Code>sortBy</Code> - {intl.formatMessage({ id: 'helperSortBy' })}
            <div style={{ marginTop: '0.25rem', marginLeft: '1rem' }}>
              <Code style={{ fontSize: '0.8rem' }}>
                {
                  '{{#each (sortBy arrayName "propertyName" "asc")}}...{{/each}}'
                }
              </Code>
            </div>
          </li>
        </ul>
      </div>

      {fields.length > 0 && (
        <div style={{ marginTop: '1rem' }}>
          <Text
            size="sm"
            fw={500}
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
            onClick={toggle}
          >
            {intl.formatMessage({ id: 'availableFieldProperties' })}
            <IconChevronDown
              size={16}
              style={{
                transform: opened ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
              }}
            />
          </Text>
          <Collapse in={opened}>
            <div style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>
              {fields.map((field, index) => {
                const properties = getFieldProperties(field);
                if (properties.length === 0) return null;

                return (
                  <div key={index} style={{ marginBottom: '0.75rem' }}>
                    <Text size="xs" fw={600} c="dimmed" mb="0.25rem">
                      {field.label || field.name || `Field ${index + 1}`} (
                      {field.type}):
                    </Text>
                    {field.type === 'signature' && (
                      <Text
                        size="xs"
                        c="dimmed"
                        mb="0.25rem"
                        style={{ fontStyle: 'italic' }}
                      >
                        {intl.formatMessage({ id: 'signatureSvgPathNote' })}
                      </Text>
                    )}
                    {field.type === 'products' && (
                      <Text
                        size="xs"
                        c="dimmed"
                        mb="0.25rem"
                        style={{ fontStyle: 'italic' }}
                      >
                        {intl.formatMessage({ id: 'productsFieldsNote' })}
                      </Text>
                    )}
                    {field.type === 'infinite-list' && (
                      <Text
                        size="xs"
                        c="dimmed"
                        mb="0.25rem"
                        style={{ fontStyle: 'italic' }}
                      >
                        {intl.formatMessage({ id: 'infiniteListFieldsNote' })}
                      </Text>
                    )}
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '0.5rem',
                      }}
                    >
                      {properties.map((prop, propIndex) => (
                        <Code key={propIndex} style={{ fontSize: '0.75rem' }}>
                          {`{{${prop}}}`}
                        </Code>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </Collapse>
        </div>
      )}
    </Alert>
  );
};
